// RecruitmentMap.jsx
// React 18 + react-leaflet v4
// Uses your efficient hook: useAdviserPostcodes() -> [{ id, postcode, status }]
// Colours:
//   - FP only  -> Pink
//   - MA only  -> Orange
//   - FP & MA  -> Purple
//   - GI/Prot   -> Yellow

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AttributionControl,
  GeoJSON,
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import RadioGroup from "../../../DesignSystem/SelectionControls/RadioGroup";
import CheckboxGroup from "../../../DesignSystem/SelectionControls/CheckboxGroup";
import InputField from "../../../DesignSystem/Inputs/InputField";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import ButtonSecondarySmall from "../../../DesignSystem/Buttons/ButtonSecondarySmall";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import { useAllAdvisersData } from "../../Hooks/GetAllAdvisers";
import { statusColor } from "./HelperFunctions/GetMarkerColor";
import "../../../../css/Adviser/PostcodeMap.css";

// ---------- Map defaults ----------
const CENTER = [51.5074, -0.1278]; // London
const UK_POSTCODE_REGEX = /^([A-Z][A-HJ-Y]?[0-9][A-Z0-9]? ?[0-9][A-Z]{2}|GIR ?0A{2})$/;
const UK_POSTCODE_CANDIDATE_REGEX = /\b(?:[A-Z][A-HJ-Y]?[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}|GIR\s?0A{2})\b/gi;
const RADIUS_OPTIONS = Array.from({ length: 10 }, (_, index) => {
  const miles = (index + 1) * 5;
  return { id: String(miles), name: `${miles} miles` };
});
const STATUS_OPTIONS = ["FP", "MA", "GI/Prot", "NAP"];
const CARTO_BASEMAP_KEY = process.env.REACT_APP_CARTO_BASEMAP_KEY;
const CARTO_BASEMAP_URL = `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_BASEMAP_KEY || "")}`;
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const COUNTY_BOUNDARY_URL =
  "https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/Upper_Tier_Local_Authorities_December_2022_Boundaries_UK_BFC/FeatureServer/0/query";
const countyBoundaryCache = new Map();

function extractUKPostcode(value) {
  const candidates = String(value || "")
    .toUpperCase()
    .match(UK_POSTCODE_CANDIDATE_REGEX) || [];
  const postcode = candidates
    .map(normalizePC)
    .find((candidate) => UK_POSTCODE_REGEX.test(candidate));
  return postcode || "";
}

// ---------- Tiny SVG pin (data URL) so no external icon deps ----------
function svgPin(color = "#FFFFFF") {
  // simple map pin shape with white dot
  const svg = `
    <svg width="36" height="52" viewBox="0 0 36 52" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 51.5c0 0 16-19.2 16-32C34 8.268 26.732 1 18 1S2 8.268 2 19.5c0 12.8 16 32 16 32z" fill="${color}" stroke="rgba(0,0,0,0.35)" />
      <path d="M18 51.5c0 0 16-19.2 16-32C34 8.268 26.732 1 18 1S2 8.268 2 19.5c0 12.8 16 32 16 32z" fill="#fff" fill-opacity="0.0" />
      <circle cx="18" cy="19" r="6.5" fill="#fff"/>
      <circle cx="18" cy="19" r="6.5" fill="${color}" fill-opacity="0.55"/>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function makeIcon(color) {
  return new L.Icon({
    iconUrl: svgPin(color),
    iconRetinaUrl: svgPin(color),
    iconSize: [24, 52],
    iconAnchor: [12, 52],
    popupAnchor: [0, -46],
  });
}

// ---------- Geocoder with tiny cache (UK: postcodes.io) ----------
const geoCache = new Map(); // key: normalized postcode -> [lat,lng] | null
function normalizePC(pc) {
  return String(pc || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}
async function geocodeUKPostcode(postcode) {
  const pc = normalizePC(postcode);
  if (!pc) return null;
  if (geoCache.has(pc)) return geoCache.get(pc);

  try {
    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`
    );
    if (!res.ok) throw new Error(`Geocode HTTP ${res.status}`);
    const json = await res.json();
    const r = json?.result;
    if (
      r &&
      typeof r.latitude === "number" &&
      typeof r.longitude === "number"
    ) {
      const ll = [r.latitude, r.longitude];
      geoCache.set(pc, ll);
      return ll;
    }
  } catch (e) {
    // console.warn("Geocoding failed for", pc, e);
  }
  geoCache.set(pc, null);
  return null;
}

const locationCache = new Map();
async function geocodeUKLocation(query) {
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) return null;

  const postcode = normalizePC(normalizedQuery);
  if (UK_POSTCODE_REGEX.test(postcode)) {
    return geocodeUKPostcode(postcode);
  }

  const cacheKey = normalizedQuery.toLowerCase();
  if (locationCache.has(cacheKey)) return locationCache.get(cacheKey);

  try {
    const params = new URLSearchParams({
      q: normalizedQuery,
      format: "jsonv2",
      countrycodes: "gb",
      limit: "1",
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      { headers: { "Accept-Language": "en-GB,en" } },
    );
    if (!response.ok) throw new Error(`Location search HTTP ${response.status}`);
    const [result] = await response.json();
    const latlng = result ? [Number(result.lat), Number(result.lon)] : null;
    locationCache.set(cacheKey, latlng);
    return latlng;
  } catch {
    return null;
  }
}

async function getCountyBoundary(postcode, [lat, lng]) {
  if (countyBoundaryCache.has(postcode)) {
    return countyBoundaryCache.get(postcode);
  }

  try {
    const params = new URLSearchParams({
      geometry: `${lng},${lat}`,
      geometryType: "esriGeometryPoint",
      inSR: "4326",
      spatialRel: "esriSpatialRelIntersects",
      outFields: "UTLA22CD,UTLA22NM",
      returnGeometry: "true",
      outSR: "4326",
      f: "geojson",
    });
    const response = await fetch(`${COUNTY_BOUNDARY_URL}?${params.toString()}`);
    if (!response.ok) throw new Error(`County boundary HTTP ${response.status}`);
    const geojson = await response.json();
    const boundary = geojson.features?.length ? geojson : null;
    countyBoundaryCache.set(postcode, boundary);
    return boundary;
  } catch {
    return null;
  }
}

function spreadSharedPostcode([lat, lng], occurrence) {
  if (occurrence === 0) return [lat, lng];

  const angle = occurrence * 2.4;
  const radius = 0.00035 * Math.ceil(occurrence / 6);
  return [
    lat + Math.sin(angle) * radius,
    lng + Math.cos(angle) * radius,
  ];
}

function distanceInMiles([lat1, lng1], [lat2, lng2]) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const latDelta = toRadians(lat2 - lat1);
  const lngDelta = toRadians(lng2 - lng1);
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(lngDelta / 2) ** 2;
  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function matchesSelectedStatus(statuses = [], selectedStatuses = []) {
  if (selectedStatuses.length === 0) return true;
  return selectedStatuses.some((selected) => {
    if (selected === "GI/Prot") {
      return statuses.some((status) =>
        ["GI", "PROT", "PROTECTION", "GI/PROT"].includes(
          String(status).toUpperCase(),
        ),
      );
    }
    return statuses.includes(selected);
  });
}

function MapViewport({ points, filterCenter }) {
  const map = useMap();

  useEffect(() => {
    if (!filterCenter) return;
    if (points.length === 0) {
      map.setView(filterCenter, 10);
      return;
    }
    map.fitBounds([filterCenter, ...points.map((point) => point.latlng)], {
      padding: [40, 40],
      maxZoom: 12,
    });
  }, [filterCenter, map, points]);

  return null;
}

// ---------- Tiny loading overlay ----------
function Loader({ show, text }) {
  if (!show) return null;
  return (
    <div className="Loader">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            border: "4px solid rgba(0,0,0,0.1)",
            borderTopColor: "#081e56",
            borderRadius: "50%",
            animation: "spin .9s linear infinite",
          }}
        />
        <div
          style={{ fontSize: 14, color: "#081e56" }}
          className="Content-SemiBold"
        >
          {text || "Loading…"}
        </div>
      </div>
    </div>
  );
}

// ---------- Component ----------
export default function RecruitmentMap() {
  // Your efficient hook → { postcodes: [{ id, postcode, status }], loading }
  const { postcodes: advisers, loading: loadingData } = useAllAdvisersData();
  const [addressType, setAddressType] = useState("residentialPostcode");

  // Resolve all postcodes to lat/lng (no auto-recenter)
  const [points, setPoints] = useState([]); // [{ id, postcode, status, latlng }]
  const [resolving, setResolving] = useState(false);
  const [filterPostcode, setFilterPostcode] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("10");
  const [filterCenter, setFilterCenter] = useState(null);
  const [filterError, setFilterError] = useState("");
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [highlightedCounty, setHighlightedCounty] = useState(null);
  const hoveredPostcodeRef = useRef(null);
  const countyFadeTimeoutRef = useRef(null);
  const countyFadeInTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (countyFadeTimeoutRef.current) {
      window.clearTimeout(countyFadeTimeoutRef.current);
    }
    if (countyFadeInTimeoutRef.current) {
      window.clearTimeout(countyFadeInTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!advisers || advisers.length === 0) {
      setPoints([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setResolving(true);
      const occurrencesByPostcode = new Map();
      const out = await Promise.all(
        advisers.map(async (p) => {
          const pc = addressType === "businessAddress"
            ? extractUKPostcode(p.businessAddress)
            : normalizePC(p.residentialPostcode || p.postcode);
          if (!pc) return null;
          const occurrence = occurrencesByPostcode.get(pc) || 0;
          occurrencesByPostcode.set(pc, occurrence + 1);
          const latlng = await geocodeUKPostcode(pc);
          if (!latlng) return null;
          return {
            id: p.id,
            postcode: pc,
            addressType,
            status: p.status || [],
            latlng: spreadSharedPostcode(latlng, occurrence),
          };
        })
      );
      if (!cancelled) {
        setPoints(out.filter(Boolean));
        setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [advisers, addressType]);

  const searchLocation = async () => {
    if (!filterPostcode.trim()) {
      setFilterCenter(null);
      setFilterError("Enter a postcode, town, city or address");
      return;
    }

    setSearchingLocation(true);
    setFilterError("");
    const latlng = await geocodeUKLocation(filterPostcode);
    setFilterCenter(latlng);
    setFilterError(latlng ? "" : "Location could not be found in the UK");
    setSearchingLocation(false);
  };

  const visiblePoints = useMemo(() => {
    const statusMatches = points.filter((point) =>
      matchesSelectedStatus(point.status, selectedStatuses),
    );
    if (!filterCenter) return statusMatches;
    const miles = Number(radiusMiles);
    return statusMatches.filter(
      (point) => distanceInMiles(filterCenter, point.latlng) <= miles,
    );
  }, [filterCenter, points, radiusMiles, selectedStatuses]);

  const resetFilters = () => {
    setAddressType("residentialPostcode");
    setFilterPostcode("");
    setRadiusMiles("10");
    setFilterCenter(null);
    setFilterError("");
    setSearchingLocation(false);
    setSelectedStatuses([]);
    setHighlightedCounty(null);
    hoveredPostcodeRef.current = null;
    if (countyFadeTimeoutRef.current) window.clearTimeout(countyFadeTimeoutRef.current);
    if (countyFadeInTimeoutRef.current) window.clearTimeout(countyFadeInTimeoutRef.current);
  };

  const highlightCounty = async (point) => {
    if (countyFadeTimeoutRef.current) {
      window.clearTimeout(countyFadeTimeoutRef.current);
      countyFadeTimeoutRef.current = null;
    }
    if (countyFadeInTimeoutRef.current) {
      window.clearTimeout(countyFadeInTimeoutRef.current);
      countyFadeInTimeoutRef.current = null;
    }
    hoveredPostcodeRef.current = point.postcode;
    const boundary = await getCountyBoundary(point.postcode, point.latlng);
    if (hoveredPostcodeRef.current !== point.postcode || !boundary) return;
    setHighlightedCounty({
      data: boundary,
      postcode: point.postcode,
      color: statusColor(point.status),
      visible: false,
    });
    countyFadeInTimeoutRef.current = window.setTimeout(() => {
      if (hoveredPostcodeRef.current === point.postcode) {
        setHighlightedCounty((current) =>
          current?.postcode === point.postcode
            ? { ...current, visible: true }
            : current,
        );
      }
      countyFadeInTimeoutRef.current = null;
    }, 30);
  };

  const clearCountyHighlight = () => {
    hoveredPostcodeRef.current = null;
    if (countyFadeInTimeoutRef.current) {
      window.clearTimeout(countyFadeInTimeoutRef.current);
      countyFadeInTimeoutRef.current = null;
    }
    setHighlightedCounty((current) =>
      current ? { ...current, visible: false } : null,
    );
    countyFadeTimeoutRef.current = window.setTimeout(() => {
      setHighlightedCounty(null);
      countyFadeTimeoutRef.current = null;
    }, 350);
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Adviser Map</title>
        <meta
          name="description"
          content="Adviser map with status-coloured markers."
        />
      </Helmet>
      <Sidebar />
      <div className="address-type-control">
        <RadioGroup
          label="Address type"
          name="adviser-address-type"
          value={addressType}
          onChange={setAddressType}
          columns={2}
          options={[
            { value: "residentialPostcode", label: "Residential" },
            { value: "businessAddress", label: "Business" },
          ]}
        />
        <CheckboxGroup
          label="Adviser status"
          options={STATUS_OPTIONS}
          values={selectedStatuses}
          onChange={setSelectedStatuses}
          columns={4}
        />
        <div className="map-radius-filter">
          <InputField
            name="map-filter-postcode"
            label="Search postcode or address"
            value={filterPostcode}
            onChange={(event) => setFilterPostcode(event.target.value)}
            placeholder="e.g. E14 5AA or London"
            error={Boolean(filterError)}
          />
          <CustomSelect
            label="Radius"
            options={RADIUS_OPTIONS}
            value={radiusMiles}
            onChange={(event) => setRadiusMiles(event.target.value)}
            clearable={false}
            searchable={false}
          />
        </div>
        {filterError && <div className="FP_Help_Text text-error">{filterError}</div>}
        <div className="map-filter-actions">
          <PrimaryButton
            type="button"
            label="Search"
            buttonText={searchingLocation ? "Searching..." : "Search"}
            onClick={searchLocation}
            disabled={searchingLocation}
          />
          <ButtonSecondarySmall label="Reset filters" onClick={resetFilters} />
        </div>
        {!loadingData && !resolving && (
          <div className="map-result-count-row">
            <div className="FP_Help_Text map-result-count">
              Showing {visiblePoints.length} of {points.length} mapped advisers
            </div>
            <div className="FP_Help_Text map-unmapped-count">
              Unmapped advisers: {Math.max(advisers.length - points.length, 0)}
            </div>
          </div>
        )}
        {/* {addressType === "businessAddress" && !loadingData && (
          <details className="map-address-diagnostics">
            <summary className="Content-Small Content-SemiBold">
              Show fetched addresses and regex results
            </summary>
            <div className="map-address-diagnostics-list">
              {businessAddressDiagnostics.map((item) => (
                <div key={item.id} className="map-address-diagnostic-row">
                  <div className="Content-Small Content-SemiBold">{item.id}</div>
                  <div className="Content-Small">
                    Fetched: {item.fetchedAddress}
                  </div>
                  <div className="Content-Small">
                    Regex result: {item.extractedPostcode || "No postcode matched"}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )} */}
      </div>
      <div className="legends-Container">
        <div className="legends-row">
          <div
            className="legends-Indicator"
            style={{ background: "#9e145bff" }}
          ></div>
          <div className="FP_Content_Semibold">FP</div>
        </div>

        <div className="legends-row">
          <div
            className="legends-Indicator"
            style={{ background: "#ed7600ff" }}
          ></div>
          <div className="FP_Content_Semibold">MA</div>
        </div>
        <div className="legends-row">
          <div
            className="legends-Indicator"
            style={{ background: "#3f1c7bff" }}
          ></div>
          <div className="FP_Content_Semibold">FP & MA</div>
        </div>
        <div className="legends-row">
          <div
            className="legends-Indicator"
            style={{ background: "#FFEE74" }}
          ></div>
          <div className="FP_Content_Semibold">GI/Prot</div>
        </div>
        <div className="legends-row">
          <div
            className="legends-Indicator"
            style={{ background: "#096039ff" }}
          ></div>
          <div className="FP_Content_Semibold">NAP</div>
        </div>
      </div>

      <div style={{ height: "100vh", width: "100%", position: "relative" }}>
        <MapContainer
          center={CENTER}
          zoom={7}
          minZoom={1}
          maxZoom={19}
          scrollWheelZoom
          attributionControl={false}
          style={{ height: "100%", width: "100%" }}
        >
          <AttributionControl prefix={false} />
          <MapViewport points={visiblePoints} filterCenter={filterCenter} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution={OSM_ATTRIBUTION}
            maxZoom={19}
          />
          {CARTO_BASEMAP_KEY && (
            <TileLayer
              key="carto-voyager-authenticated"
              url={CARTO_BASEMAP_URL}
              attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
              subdomains="abcd"
              maxZoom={19}
            />
          )}

          {highlightedCounty && (
            <GeoJSON
              key={highlightedCounty.postcode}
              data={highlightedCounty.data}
              className="county-highlight-layer"
              style={{
                color: highlightedCounty.color,
                weight: 1,
                opacity: highlightedCounty.visible ? 1 : 0,
                fillColor: highlightedCounty.color,
                fillOpacity: highlightedCounty.visible ? 0.18 : 0,
              }}
              interactive={false}
            />
          )}

          {visiblePoints.map((p) => {
            const icon = makeIcon(statusColor(p.status));
            return (
              <Marker
                key={p.id}
                position={p.latlng}
                icon={icon}
                eventHandlers={{
                  mouseover: (e) => {
                    e.target.openPopup();
                    highlightCounty(p);
                  },
                  mouseout: clearCountyHighlight,
                }}
              >
                <Popup>
                  <div className="PopUp">
                    <div
                      className="FP_Content_Semibold text-primary-1"
                      style={{ marginBottom: 5 }}
                    >
                      <a
                        href={`mailto:${p.id}`}
                        style={{
                          color: "inherit",
                        }}
                      >
                        {p.id}
                      </a>
                    </div>
                    <div className="FP_Help_Text_Semibold">Postcode: {p.postcode}</div>
                    <div className="FP_Help_Text_Semibold">
                      Status:{" "}
                      {Array.isArray(p.status) && p.status.length
                        ? p.status.join(", ")
                        : "None"}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Loading overlay */}
        <Loader show={loadingData || resolving} text="" />
      </div>
    </div>
  );
}
